import { useEffect, useCallback, useMemo, useState, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  addEdge,
  Handle,
  Position,
  useReactFlow,
  getNodesBounds,
  getViewportForBounds,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useMindmap } from '../contexts/MindmapContext';
import { localMindmapService } from '../services/localMindmapService';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  Download,
  Image,
  Save,
  FolderOpen,
  MoreHorizontal,
  Check,
  X,
} from 'lucide-react';

function createClientId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// Custom Mindmap Node Component
function MindmapNode({ id, data, selected }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(data?.label || '');
  const inputRef = useRef(null);

  const focusInput = useCallback((selectAll = true) => {
    if (!inputRef.current) {
      return;
    }

    inputRef.current.focus();
    if (selectAll) {
      inputRef.current.select();
    }
  }, []);

  useEffect(() => {
    setEditValue(data?.label || '');
  }, [data?.label]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    let frameId;
    let attempts = 0;

    const focusInput = () => {
      attempts += 1;

      focusInputRef();

      if (attempts < 6 && document.activeElement !== inputRef.current) {
        frameId = window.requestAnimationFrame(focusInput);
      }
    };

    frameId = window.requestAnimationFrame(focusInput);

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [focusInput, isEditing]);

  const focusInputRef = (selectAll = true) => {
    focusInput(selectAll);
  };

  useEffect(() => {
    if (data?.startEditing) {
      setIsEditing(true);
      setEditValue(data?.label || '');

      const timeoutId = window.setTimeout(() => {
        data?.onStartEditingHandled?.(id);
      }, 120);

      return () => {
        window.clearTimeout(timeoutId);
      };
    }
  }, [data, data?.label, data?.startEditing, id]);

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    flushSync(() => {
      setIsEditing(true);
      setEditValue(data?.label || '');
    });
    focusInputRef();
  };

  const handleTapToFocusInput = (e) => {
    if (!isEditing) {
      return;
    }
    e.stopPropagation();
    focusInputRef(false);
  };

  const handleSave = (e) => {
    e.stopPropagation();
    const nextLabel = editValue.trim() || 'Untitled Idea';
    data?.onLabelChange?.(id, nextLabel);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave(e);
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditValue(data?.label || '');
    }
  };

  const handleInputClick = (e) => {
    e.stopPropagation();
  };

  const handleAddFromDirection = (e, direction) => {
    e.stopPropagation();
    data?.onAddChild?.(id, direction);
  };

  const handleDeleteTap = (e) => {
    e.stopPropagation();
    data?.onRequestDelete?.(id);
  };

  const handleToggleCollapseTap = (e) => {
    e.stopPropagation();
    data?.onToggleCollapse?.(id);
  };

  const handleEditNoteTap = (e) => {
    e.stopPropagation();
    data?.onEditNote?.(id, data?.note || '');
  };

  const isRoot = data?.isRoot;

  return (
    <div
      className={`mindmap-node relative ${isRoot ? 'root' : ''} ${selected ? 'selected' : ''}`}
      onDoubleClick={handleDoubleClick}
    >
      <Handle id="t-top" type="target" position={Position.Top} className="!opacity-0 !pointer-events-none" />
      <Handle id="t-right" type="target" position={Position.Right} className="!opacity-0 !pointer-events-none" />
      <Handle id="t-bottom" type="target" position={Position.Bottom} className="!opacity-0 !pointer-events-none" />
      <Handle id="t-left" type="target" position={Position.Left} className="!opacity-0 !pointer-events-none" />
      
      {isEditing ? (
        <div
          className="flex items-center gap-1"
          onClick={handleInputClick}
          onTouchStart={handleTapToFocusInput}
          onTouchEnd={handleTapToFocusInput}
        >
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            onTouchStart={handleTapToFocusInput}
            autoFocus
            inputMode="text"
            enterKeyHint="done"
            className="px-2 py-1 text-sm border-2 border-primary rounded outline-none w-32 text-gray-900 bg-white"
            style={{ maxWidth: isRoot ? 200 : 150 }}
          />
          <button onClick={handleSave} className="p-1 hover:bg-green-100 rounded text-green-600">
            <Check className="w-3 h-3" />
          </button>
          <button onClick={() => { setIsEditing(false); setEditValue(data?.label || ''); }} className="p-1 hover:bg-red-100 rounded text-red-500">
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <span className={`block truncate ${isRoot ? 'text-white' : 'text-gray-900'}`}>{data?.label || ''}</span>
      )}

      <Handle id="s-top" type="source" position={Position.Top} className="!opacity-0 !pointer-events-none" />
      <Handle id="s-right" type="source" position={Position.Right} className="!opacity-0 !pointer-events-none" />
      <Handle id="s-bottom" type="source" position={Position.Bottom} className="!opacity-0 !pointer-events-none" />
      <Handle id="s-left" type="source" position={Position.Left} className="!opacity-0 !pointer-events-none" />

      {data?.showAddButtons && (
        <>
          <button
            type="button"
            onClick={(e) => handleAddFromDirection(e, 'top')}
            className="node-add-button node-add-button-top nodrag"
            aria-label="Add node above"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={(e) => handleAddFromDirection(e, 'right')}
            className="node-add-button node-add-button-right nodrag"
            aria-label="Add node to the right"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={(e) => handleAddFromDirection(e, 'bottom')}
            className="node-add-button node-add-button-bottom nodrag"
            aria-label="Add node below"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={(e) => handleAddFromDirection(e, 'left')}
            className="node-add-button node-add-button-left nodrag"
            aria-label="Add node to the left"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </>
      )}

      {!isEditing && (
        <button
          type="button"
          onClick={handleEditNoteTap}
          className={`absolute top-1 right-1 w-4 h-4 rounded-full border text-[8px] font-semibold leading-none flex items-center justify-center z-20 nodrag transition-colors ${
            data?.note?.trim()
              ? 'bg-emerald-100 border-emerald-300 text-emerald-700'
              : 'bg-white border-gray-300 text-gray-400 hover:text-gray-600'
          }`}
          aria-label={data?.note?.trim() ? 'Edit note' : 'Add note'}
          title={data?.note?.trim() ? 'Edit note' : 'Add note'}
        >
          N
        </button>
      )}

      {data?.isMobileViewport && selected && !isRoot && !isEditing && (
        <button
          type="button"
          onClick={handleDeleteTap}
          className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-red-500 text-white shadow-md flex items-center justify-center z-20 nodrag"
          aria-label="Delete node"
          title="Delete node"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}

      {data?.hasChildren && !isEditing && (
        <button
          type="button"
          onClick={handleToggleCollapseTap}
          className={`absolute -right-1 -bottom-1 min-w-4 h-4 px-0.5 rounded-full border shadow-sm/20 flex items-center justify-center gap-0.5 z-20 nodrag transition-colors ${
            data?.collapsedDescendantCount
              ? 'bg-amber-100 border-amber-200 text-amber-700 hover:bg-amber-200'
              : 'bg-white border-gray-300 text-gray-500 hover:bg-white hover:text-gray-600'
          }`}
          title={data?.collapsedDescendantCount
            ? `Expand ${data.collapsedDescendantCount} hidden node${data.collapsedDescendantCount === 1 ? '' : 's'}`
            : 'Collapse branch'}
          aria-label={data?.collapsedDescendantCount ? 'Expand collapsed branch' : 'Collapse branch'}
        >
          {data?.collapsedDescendantCount ? <ChevronRight className="w-2 h-2" /> : <ChevronDown className="w-2 h-2" />}
          {Boolean(data?.collapsedDescendantCount) && (
            <span className="text-[8px] font-semibold leading-none">{data.collapsedDescendantCount}</span>
          )}
        </button>
      )}
    </div>
  );
}

const nodeTypes = {
  mindmap: MindmapNode,
};

function getDirectionalHandles(sourceNode, targetNode) {
  const sx = sourceNode?.position?.x || 0;
  const sy = sourceNode?.position?.y || 0;
  const tx = targetNode?.position?.x || 0;
  const ty = targetNode?.position?.y || 0;
  const dx = tx - sx;
  const dy = ty - sy;

  if (Math.abs(dx) > Math.abs(dy)) {
    if (dx >= 0) {
      return { sourceHandle: 's-right', targetHandle: 't-left' };
    }
    return { sourceHandle: 's-left', targetHandle: 't-right' };
  }

  if (dy >= 0) {
    return { sourceHandle: 's-bottom', targetHandle: 't-top' };
  }
  return { sourceHandle: 's-top', targetHandle: 't-bottom' };
}

function getStableEdgeHandles(edge, nodeById) {
  const persistedSourceHandle = edge?.sourceHandle ?? edge?.source_handle ?? edge?.data?.sourceHandle ?? edge?.data?.source_handle;
  const persistedTargetHandle = edge?.targetHandle ?? edge?.target_handle ?? edge?.data?.targetHandle ?? edge?.data?.target_handle;

  if (persistedSourceHandle && persistedTargetHandle) {
    return {
      sourceHandle: persistedSourceHandle,
      targetHandle: persistedTargetHandle,
    };
  }

  return getDirectionalHandles(nodeById.get(edge.source), nodeById.get(edge.target));
}

const NODE_HORIZONTAL_CLEARANCE = 170;
const NODE_VERTICAL_CLEARANCE = 110;

function isPositionOccupied(candidatePosition, nodes, ignoredNodeId = null) {
  return nodes.some((node) => {
    if (ignoredNodeId && node.id === ignoredNodeId) {
      return false;
    }

    const nodeX = node?.position?.x ?? 0;
    const nodeY = node?.position?.y ?? 0;
    return (
      Math.abs(nodeX - candidatePosition.x) < NODE_HORIZONTAL_CLEARANCE
      && Math.abs(nodeY - candidatePosition.y) < NODE_VERTICAL_CLEARANCE
    );
  });
}

function findNearestFreePosition(preferredPosition, nodes, parentNode) {
  if (!isPositionOccupied(preferredPosition, nodes)) {
    return preferredPosition;
  }

  const parentX = parentNode?.position?.x ?? 0;
  const parentY = parentNode?.position?.y ?? 0;

  for (let ring = 1; ring <= 10; ring += 1) {
    const radius = ring * 44;
    const sampleCount = 12 + ring * 4;

    for (let i = 0; i < sampleCount; i += 1) {
      const angle = (i / sampleCount) * Math.PI * 2;
      const candidate = {
        x: Math.round(preferredPosition.x + Math.cos(angle) * radius),
        y: Math.round(preferredPosition.y + Math.sin(angle) * radius),
      };

      const dxFromParent = candidate.x - parentX;
      const dyFromParent = candidate.y - parentY;
      const distanceFromParent = Math.sqrt(dxFromParent * dxFromParent + dyFromParent * dyFromParent);
      if (distanceFromParent < 120) {
        continue;
      }

      if (!isPositionOccupied(candidate, nodes)) {
        return candidate;
      }
    }
  }

  return preferredPosition;
}

// Inner canvas component that uses React Flow hooks
function MindmapCanvas({ 
  id, 
  nodes, 
  edges, 
  setNodes, 
  setEdges, 
  onNodesChange, 
  onEdgesChange, 
  title, 
  isEditingTitle, 
  setIsEditingTitle, 
  setTitle, 
  handleSaveTitle, 
  handleAddChildNode,
  onNodeDragStop,
  handleDeleteNode, 
  isMobileViewport,
  mobileAddButtonsEnabled,
  setMobileAddButtonsEnabled,
  showExportMenu, 
  setShowExportMenu, 
  handleExportJSON, 
  handleExportPNG,
  handleSaveLocal,
  handleLoadLocal,
  syncStatus,
  shouldSaveLocally,
}) {
  const navigate = useNavigate();
  const { fitView } = useReactFlow();
  const reactFlowWrapper = useRef(null);
  const mobileHasAutoFittedRef = useRef(false);

  useEffect(() => {
    mobileHasAutoFittedRef.current = false;
  }, [id]);

  // Connect nodes
  const onConnect = useCallback((params) => {
    const sourceNode = nodes.find((n) => n.id === params.source);
    const targetNode = nodes.find((n) => n.id === params.target);
    const { sourceHandle, targetHandle } = getDirectionalHandles(sourceNode, targetNode);
    const newEdge = {
      ...params,
      id: `e-${params.source}-${params.target}`,
      sourceHandle: params.sourceHandle || sourceHandle,
      targetHandle: params.targetHandle || targetHandle,
      type: 'default',
      animated: false,
      style: { stroke: '#3d9168', strokeWidth: 2 },
    };
    setEdges((eds) => {
      // Check if edge already exists
      const exists = eds.some(e => 
        (e.source === params.source && e.target === params.target) ||
        (e.source === params.target && e.target === params.source)
      );
      if (exists) return eds;
      return addEdge(newEdge, eds);
    });
  }, [nodes, setEdges]);

  // Fit view on load
  useEffect(() => {
    if (nodes.length === 0) {
      return;
    }

    if (isMobileViewport && mobileHasAutoFittedRef.current) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      fitView({ padding: 0.2, duration: 300 });
      if (isMobileViewport) {
        mobileHasAutoFittedRef.current = true;
      }
    }, 100);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [fitView, isMobileViewport, nodes.length]);

  return (
    <div className="h-full flex flex-col" ref={reactFlowWrapper}>
      {/* Toolbar */}
      <div className="bg-white border-b px-4 py-2 flex items-center justify-between z-10 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          
          {isEditingTitle ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={handleSaveTitle}
                onKeyDown={(e) => e.key === 'Enter' && handleSaveTitle()}
                className="px-2 py-1 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-gray-900"
                autoFocus
              />
            </div>
          ) : (
            <h2
              className="font-medium cursor-pointer hover:text-primary text-gray-900"
              onClick={() => setIsEditingTitle(true)}
            >
              {title || 'Untitled Mindmap'}
            </h2>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs ${
              syncStatus === 'error'
                ? 'border-red-200 bg-red-50 text-red-700'
                : syncStatus === 'pending'
                  ? 'border-amber-200 bg-amber-50 text-amber-700'
                  : syncStatus === 'saving'
                    ? 'border-blue-200 bg-blue-50 text-blue-700'
                    : 'border-emerald-200 bg-emerald-50 text-emerald-700'
            }`}
            title={shouldSaveLocally ? 'Local storage sync status' : 'Cloud sync status'}
          >
            <span
              className={`inline-block w-2 h-2 rounded-full ${
                syncStatus === 'error'
                  ? 'bg-red-500'
                  : syncStatus === 'pending'
                    ? 'bg-amber-500'
                    : syncStatus === 'saving'
                      ? 'bg-blue-500 animate-pulse'
                      : 'bg-emerald-500'
              }`}
            />
            <span className="hidden sm:inline">
              {syncStatus === 'error'
                ? 'Save failed'
                : syncStatus === 'pending'
                  ? 'Changes pending'
                  : syncStatus === 'saving'
                    ? 'Saving...'
                    : 'All changes saved'}
            </span>
          </div>

          {isMobileViewport && (
            <button
              onClick={() => setMobileAddButtonsEnabled((enabled) => !enabled)}
              className="px-3 py-1.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
              title="Toggle node add buttons"
            >
              {mobileAddButtonsEnabled ? 'Hide +' : 'Show +'}
            </button>
          )}

          <button
            onClick={handleAddChildNode}
            className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Child</span>
          </button>

          <button
            onClick={handleDeleteNode}
            className="p-2 hover:bg-red-50 text-red-500 rounded-lg transition-colors"
            title="Delete selected"
          >
            <Trash2 className="w-4 h-4" />
          </button>

          {/* Export menu */}
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <MoreHorizontal className="w-5 h-5" />
            </button>
            
            {showExportMenu && (
              <>
                <div className="fixed inset-0" onClick={() => setShowExportMenu(false)} />
                <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border py-1 min-w-32 z-50">
                  <button
                    onClick={handleExportJSON}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                  >
                    <Download className="w-4 h-4" />
                    Export JSON
                  </button>
                  <button
                    onClick={handleExportPNG}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                  >
                    <Image className="w-4 h-4" />
                    Export PNG
                  </button>
                  <button
                    onClick={handleSaveLocal}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                  >
                    <Save className="w-4 h-4" />
                    Save Local
                  </button>
                  <button
                    onClick={handleLoadLocal}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                  >
                    <FolderOpen className="w-4 h-4" />
                    Load Local
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Instructions */}
      {nodes.length === 1 && (
        <div className="bg-primary/10 border-b px-4 py-2 text-sm text-gray-700 text-center">
          Use the + buttons around the center node to add ideas quickly
        </div>
      )}

      {/* Canvas */}
      <div className="flex-1 touch-controls min-h-0 bg-gray-50">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeDragStop={onNodeDragStop}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          fitView
          minZoom={0.1}
          maxZoom={2}
          defaultEdgeOptions={{
            type: 'default',
            animated: false,
            style: { stroke: '#3d9168', strokeWidth: 2 },
          }}
          proOptions={{ hideAttribution: true }}
          onConnectStart={() => {}}
          onConnectEnd={() => {}}
        >
          <Background color="#e5e7eb" gap={20} />
          <Controls showInteractive={false} className="bg-white rounded-lg shadow" />
        </ReactFlow>
      </div>
    </div>
  );
}

export default function EditorPage() {
  const { id } = useParams();
  const { currentMindmap, loading, loadMindmap, updateMindmap, updateNode, createNode, deleteNode, exportMindmap } = useMindmap();
  const [nodes, setNodes, baseOnNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [title, setTitle] = useState('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [saveLocation, setSaveLocation] = useState('cloud');
  const [syncStatus, setSyncStatus] = useState('saved');
  const [isMobileViewport, setIsMobileViewport] = useState(() => (typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false));
  const [mobileAddButtonsEnabled, setMobileAddButtonsEnabled] = useState(true);
  const [noteModal, setNoteModal] = useState({
    isOpen: false,
    nodeId: null,
    nodeLabel: '',
    value: '',
  });
  const titleRef = useRef('');
  const nodesRef = useRef([]);
  const edgesRef = useRef([]);
  const hydratedServerSnapshotRef = useRef(null);

  const isLocalOnlyMap = id?.startsWith('local-');
  const shouldSaveLocally = isLocalOnlyMap || saveLocation === 'local';

  useEffect(() => {
    if (!id) return;
    if (id.startsWith('local-')) {
      setSaveLocation('local');
      return;
    }
    setSaveLocation(localMindmapService.getSavePreference(id, 'cloud'));
  }, [id]);

  useEffect(() => {
    setSyncStatus('saved');
  }, [id, shouldSaveLocally]);

  const showNodeAddButtons = isMobileViewport && mobileAddButtonsEnabled;

  useEffect(() => {
    hydratedServerSnapshotRef.current = null;
  }, [id]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const onMediaChange = (event) => {
      setIsMobileViewport(event.matches);
    };

    setIsMobileViewport(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', onMediaChange);
      return () => mediaQuery.removeEventListener('change', onMediaChange);
    }

    mediaQuery.addListener(onMediaChange);
    return () => mediaQuery.removeListener(onMediaChange);
  }, []);

  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);

  useEffect(() => {
    titleRef.current = title;
  }, [title]);

  const buildLocalSnapshotPayload = useCallback((nextTitle, nextNodes, nextEdges) => {
    const parentByTarget = new Map((nextEdges || []).map((edge) => [edge.target, edge.source]));

    return {
    version: '1.0',
    mindmapId: id,
    title: nextTitle,
    nodes: nextNodes.map((node) => ({
      id: node.id,
      type: node.type,
      parentId: node?.data?.parentId ?? node.parent_id ?? parentByTarget.get(node.id) ?? null,
      position: node.position,
      data: {
        ...node.data,
        onLabelChange: undefined,
        onEditNote: undefined,
        onAddChild: undefined,
        onStartEditingHandled: undefined,
        showAddButtons: undefined,
        startEditing: undefined,
        isRoot: undefined,
      },
      selected: false,
    })),
    edges: nextEdges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.sourceHandle,
      targetHandle: edge.targetHandle,
      type: edge.type,
      animated: edge.animated,
    })),
    };
  }, [id]);

  const persistNodeUpdate = useCallback(async (nodeId, updates) => {
    if (shouldSaveLocally) {
      return;
    }

    setSyncStatus('saving');
    try {
      const updated = await updateNode(nodeId, updates);
      if (!updated) {
        setSyncStatus('error');
        return;
      }

      setNodes((nds) => nds.map((node) => {
        if (node.id !== nodeId) return node;
        return {
          ...node,
          position: updated.position || node.position,
          data: {
            ...node.data,
            ...(updated.data || {}),
          },
        };
      }));
      setSyncStatus('saved');
    } catch (err) {
      console.error('Cloud node save failed:', err);
      setSyncStatus('error');
    }
  }, [setNodes, shouldSaveLocally, updateNode]);

  const handleNodeLabelChange = useCallback((nodeId, label) => {
    setNodes((nds) => nds.map((node) => (
      node.id === nodeId
        ? { ...node, data: { ...node.data, label } }
        : node
    )));

    void persistNodeUpdate(nodeId, { data: { label } });
  }, [persistNodeUpdate, setNodes]);

  const handleNodeNoteEdit = useCallback((nodeId, currentNote = '') => {
    const node = nodesRef.current.find((candidate) => candidate.id === nodeId);
    setNoteModal({
      isOpen: true,
      nodeId,
      nodeLabel: node?.data?.label || 'Node',
      value: currentNote || '',
    });
  }, []);

  const handleCloseNoteModal = useCallback(() => {
    setNoteModal({
      isOpen: false,
      nodeId: null,
      nodeLabel: '',
      value: '',
    });
  }, []);

  const handleSaveNodeNote = useCallback(() => {
    if (!noteModal.nodeId) {
      handleCloseNoteModal();
      return;
    }

    const nodeId = noteModal.nodeId;
    const note = noteModal.value.trim();

    setNodes((nds) => nds.map((node) => (
      node.id === nodeId
        ? { ...node, data: { ...node.data, note } }
        : node
    )));

    void persistNodeUpdate(nodeId, { data: { note } });
    handleCloseNoteModal();
  }, [handleCloseNoteModal, noteModal.nodeId, noteModal.value, persistNodeUpdate, setNodes]);

  useEffect(() => {
    if (!noteModal.isOpen) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleCloseNoteModal();
      }

      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        handleSaveNodeNote();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleCloseNoteModal, handleSaveNodeNote, noteModal.isOpen]);

  const onNodeDragStop = useCallback((_, node) => {
    void persistNodeUpdate(node.id, { position: node.position });
  }, [persistNodeUpdate]);

  const handleStartEditingHandled = useCallback((nodeId) => {
    setNodes((nds) => nds.map((node) => {
      if (node.id !== nodeId || !node.data?.startEditing) return node;
      return {
        ...node,
        data: {
          ...node.data,
          startEditing: false,
        },
      };
    }));
  }, [setNodes]);

  const deleteNodesWithDescendants = useCallback(async (nodeIds) => {
    if (!nodeIds?.length) return;

    const currentNodes = nodesRef.current;
    const currentEdges = edgesRef.current;

    const nodesToDelete = new Set(
      nodeIds.filter((nodeId) => {
        const node = currentNodes.find((n) => n.id === nodeId);
        return node && !node.data?.isRoot;
      })
    );
    if (nodesToDelete.size === 0) return;

    const childrenByParentId = new Map();
    currentNodes.forEach((node) => {
      const parentId = node?.data?.parentId;
      if (!parentId) return;
      const siblings = childrenByParentId.get(parentId) || [];
      siblings.push(node.id);
      childrenByParentId.set(parentId, siblings);
    });

    const findDescendants = (targetNodeIds) => {
      const childrenFromEdges = currentEdges
        .filter((e) => targetNodeIds.includes(e.source))
        .map((e) => e.target);

      const childrenFromHierarchy = targetNodeIds.flatMap((targetNodeId) => (
        childrenByParentId.get(targetNodeId) || []
      ));

      const children = Array.from(new Set([...childrenFromEdges, ...childrenFromHierarchy]));
      children.forEach((childId) => {
        if (!nodesToDelete.has(childId)) {
          nodesToDelete.add(childId);
          findDescendants([childId]);
        }
      });
    };
    findDescendants(Array.from(nodesToDelete));

    const edgesToDelete = currentEdges
      .filter((e) => nodesToDelete.has(e.source) || nodesToDelete.has(e.target))
      .map((e) => e.id);

    const deletionOrder = [];
    const remaining = new Set(nodesToDelete);
    while (remaining.size > 0) {
      let removedInPass = false;
      for (const nodeId of Array.from(remaining)) {
        const hasChildInRemaining = currentEdges.some(
          (edge) => edge.source === nodeId && remaining.has(edge.target)
        );
        if (!hasChildInRemaining) {
          deletionOrder.push(nodeId);
          remaining.delete(nodeId);
          removedInPass = true;
        }
      }

      if (!removedInPass) {
        deletionOrder.push(...remaining);
        break;
      }
    }

    const nextNodes = currentNodes.filter((n) => !nodesToDelete.has(n.id));
    const nextEdges = currentEdges.filter((e) => !edgesToDelete.includes(e.id));

    setNodes(nextNodes);
    setEdges(nextEdges);

    if (shouldSaveLocally) {
      setSyncStatus('saving');
      try {
        const payload = buildLocalSnapshotPayload(titleRef.current, nextNodes, nextEdges);
        localMindmapService.saveLocalMindmapData(id, payload, { updateIndex: isLocalOnlyMap });
        setSyncStatus('saved');
      } catch (err) {
        console.error('Local delete autosave failed:', err);
        setSyncStatus('error');
      }
      return;
    }

    setSyncStatus('saving');
    try {
      for (const nodeId of deletionOrder) {
        const deleted = await deleteNode(nodeId);
        if (!deleted) {
          throw new Error(`Failed to delete node ${nodeId}`);
        }
      }
      setSyncStatus('saved');
    } catch (err) {
      console.error('Cloud node delete failed:', err);
      setSyncStatus('error');
    }
  }, [buildLocalSnapshotPayload, deleteNode, id, isLocalOnlyMap, setEdges, setNodes, shouldSaveLocally]);

  const handleDeleteNodeById = useCallback((nodeId) => {
    void deleteNodesWithDescendants([nodeId]);
  }, [deleteNodesWithDescendants]);

  const handleDeleteNode = async () => {
    const selectedNodeIds = nodesRef.current
      .filter((node) => node.selected)
      .map((node) => node.id);
    await deleteNodesWithDescendants(selectedNodeIds);
  };

  const onNodesChange = useCallback((changes) => {
    const removedNodeIds = changes
      .filter((change) => change.type === 'remove')
      .map((change) => change.id);

    if (removedNodeIds.length > 0) {
      void deleteNodesWithDescendants(removedNodeIds);
    }

    const nonRemovalChanges = changes.filter((change) => change.type !== 'remove');
    if (nonRemovalChanges.length > 0) {
      baseOnNodesChange(nonRemovalChanges);
    }
  }, [baseOnNodesChange, deleteNodesWithDescendants]);

  const handleToggleCollapse = useCallback((nodeId) => {
    const currentNodes = nodesRef.current;
    const currentEdges = edgesRef.current;
    const currentNode = currentNodes.find((node) => node.id === nodeId);
    if (!currentNode) {
      return;
    }

    const hasChildren = currentNodes.some((node) => node?.data?.parentId === nodeId)
      || currentEdges.some((edge) => edge.source === nodeId);
    if (!hasChildren) {
      return;
    }

    const nextCollapsed = !Boolean(currentNode?.data?.collapsed);
    const nextData = {
      ...(currentNode.data || {}),
      collapsed: nextCollapsed,
    };

    setNodes((nds) => nds.map((node) => (
      node.id === nodeId
        ? { ...node, data: { ...node.data, collapsed: nextCollapsed } }
        : node
    )));

    if (!shouldSaveLocally) {
      void persistNodeUpdate(nodeId, { data: nextData });
    }
  }, [persistNodeUpdate, setNodes, shouldSaveLocally]);

  // Add child node to selected parent node or explicit parent/direction
  const handleAddChildNode = useCallback(async (parentNodeId = null, direction = 'auto') => {
    const currentNodes = nodesRef.current;
    const currentEdges = edgesRef.current;

    const selectedNode = currentNodes.find((n) => n.selected);
    const parentNode = parentNodeId
      ? currentNodes.find((n) => n.id === parentNodeId)
      : (selectedNode || currentNodes.find((n) => n.data?.isRoot));

    if (!parentNode) {
      return;
    }

    const directionVectors = {
      top: { x: 0, y: -1 },
      right: { x: 1, y: 0 },
      bottom: { x: 0, y: 1 },
      left: { x: -1, y: 0 },
    };

    let newX;
    let newY;

    if (directionVectors[direction]) {
      const distance = 170;
      const vector = directionVectors[direction];
      newX = parentNode.position.x + vector.x * distance;
      newY = parentNode.position.y + vector.y * distance;
    } else {
      const siblings = currentNodes.filter((n) => currentEdges.some((e) => e.source === parentNode.id && e.target === n.id));
      const angle = (siblings.length * 45 - 90) * (Math.PI / 180);
      const distance = 180;
      newX = parentNode.position.x + Math.cos(angle) * distance;
      newY = parentNode.position.y + Math.sin(angle) * distance;
    }

    const preferredPosition = { x: Math.round(newX), y: Math.round(newY) };
    const freePosition = findNearestFreePosition(preferredPosition, currentNodes, parentNode);

    let created;
    if (shouldSaveLocally) {
      created = {
        id: createClientId(),
        position: freePosition,
        data: { label: 'New Idea' },
      };
    } else {
      setSyncStatus('saving');
      const predictedHandles = getDirectionalHandles(parentNode, { position: freePosition });
      try {
        created = await createNode(id, {
          parentId: parentNode.id,
          position: freePosition,
          data: { label: 'New Idea' },
          edgeData: predictedHandles,
        });
      } catch (err) {
        console.error('Cloud node create failed:', err);
        setSyncStatus('error');
        return;
      }

      if (!created) {
        setSyncStatus('error');
        return;
      }

      setSyncStatus('saved');
    }

    const newNode = {
      id: created.id,
      type: 'mindmap',
      position: created.position || freePosition,
      data: {
        ...(created.data || {}),
        isRoot: false,
        parentId: parentNode.id,
        showAddButtons: showNodeAddButtons,
        isMobileViewport,
        startEditing: true,
        onStartEditingHandled: handleStartEditingHandled,
        onLabelChange: handleNodeLabelChange,
        onEditNote: handleNodeNoteEdit,
        onAddChild: (parentId, childDirection) => {
          void handleAddChildNode(parentId, childDirection);
        },
        onRequestDelete: (targetId) => {
          handleDeleteNodeById(targetId);
        },
      },
    };

    const newEdge = {
      id: `e-${parentNode.id}-${created.id}`,
      source: parentNode.id,
      target: created.id,
      ...getDirectionalHandles(parentNode, newNode),
      type: 'default',
      animated: false,
      style: { stroke: '#3d9168', strokeWidth: 2 },
    };

    setNodes((nds) => [...nds, newNode]);
    setEdges((eds) => {
      const exists = eds.some((edge) => edge.source === newEdge.source && edge.target === newEdge.target);
      if (exists) return eds;
      return [...eds, newEdge];
    });
  }, [createNode, id, setEdges, setNodes, handleNodeLabelChange, handleNodeNoteEdit, showNodeAddButtons, handleStartEditingHandled, shouldSaveLocally, isMobileViewport, handleDeleteNodeById]);

  useEffect(() => {
    setNodes((nds) => nds.map((node) => ({
      ...node,
      data: {
        ...node.data,
        showAddButtons: showNodeAddButtons,
        isMobileViewport,
        onEditNote: handleNodeNoteEdit,
        onStartEditingHandled: handleStartEditingHandled,
        onRequestDelete: (targetId) => {
          handleDeleteNodeById(targetId);
        },
      },
    })));
  }, [setNodes, showNodeAddButtons, isMobileViewport, handleNodeNoteEdit, handleStartEditingHandled, handleDeleteNodeById]);

  useEffect(() => {
    const onTabCreateChild = (event) => {
      if (isMobileViewport || event.key !== 'Tab') {
        return;
      }

      const target = event.target;
      if (target instanceof HTMLElement) {
        const typingTags = ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'];
        if (typingTags.includes(target.tagName) || target.isContentEditable) {
          return;
        }
      }

      const selectedNodes = nodesRef.current.filter((node) => node.selected);
      if (selectedNodes.length !== 1) {
        return;
      }

      event.preventDefault();
      const direction = event.shiftKey ? 'left' : 'right';
      void handleAddChildNode(selectedNodes[0].id, direction);
    };

    window.addEventListener('keydown', onTabCreateChild);
    return () => {
      window.removeEventListener('keydown', onTabCreateChild);
    };
  }, [handleAddChildNode, isMobileViewport]);

  // Load mindmap data
  useEffect(() => {
    if (!id) {
      return;
    }

    if (isLocalOnlyMap) {
      const localData = localMindmapService.loadLocalMindmapData(id);
      if (!localData) {
        const created = localMindmapService.createLocalMindmap('Untitled Mindmap');
        if (created.id !== id) {
          return;
        }
      }
      return;
    }

    if (!isLocalOnlyMap) {
      loadMindmap(id);
    }
  }, [id, isLocalOnlyMap, loadMindmap]);

  // Initialize nodes and edges when mindmap data changes
  useEffect(() => {
    if (isLocalOnlyMap) {
      const localData = localMindmapService.loadLocalMindmapData(id);
      if (!localData) {
        setTitle('Untitled Mindmap');
        setNodes([]);
        setEdges([]);
        return;
      }

      setTitle(localData.title || 'Untitled Mindmap');
      const parentByTarget = new Map((localData.edges || []).map((edge) => [edge.target, edge.source]));
      const flowNodes = (localData.nodes || []).map((node) => ({
        id: node.id,
        type: node.type || 'mindmap',
        position: node.position || { x: 0, y: 0 },
        data: {
          ...(node.data || {}),
          parentId: node.parent_id ?? node.parentId ?? parentByTarget.get(node.id) ?? null,
          isRoot: !(node.parent_id ?? node.parentId ?? parentByTarget.get(node.id) ?? null),
          showAddButtons: showNodeAddButtons,
          isMobileViewport,
          startEditing: false,
          onStartEditingHandled: handleStartEditingHandled,
          onLabelChange: handleNodeLabelChange,
          onEditNote: handleNodeNoteEdit,
          onAddChild: (parentId, direction) => {
            void handleAddChildNode(parentId, direction);
          },
          onRequestDelete: (targetId) => {
            handleDeleteNodeById(targetId);
          },
        },
        selected: false,
      }));

      const nodeById = new Map(flowNodes.map((node) => [node.id, node]));
      const flowEdges = (localData.edges || []).map((edge) => ({
        ...getStableEdgeHandles(edge, nodeById),
        id: edge.id,
        source: edge.source,
        target: edge.target,
        type: 'default',
        animated: Boolean(edge.animated),
        style: { stroke: '#3d9168', strokeWidth: 2 },
        selected: false,
      }));

      setNodes(flowNodes);
      setEdges(flowEdges);
      return;
    }

    if (currentMindmap?.mindmap && currentMindmap?.nodes) {
      const serverSnapshotKey = [
        id,
        currentMindmap?.mindmap?.updated_at || currentMindmap?.mindmap?.created_at || '',
        currentMindmap?.nodes?.length || 0,
        currentMindmap?.edges?.length || 0,
      ].join(':');

      if (hydratedServerSnapshotRef.current === serverSnapshotKey) {
        return;
      }

      setTitle(currentMindmap.mindmap.title || 'Untitled Mindmap');
      
      // Convert API nodes to React Flow nodes
      const flowNodes = currentMindmap.nodes.map(node => ({
        id: node.id,
        type: 'mindmap',
        position: node.position || { x: node.position_x || 0, y: node.position_y || 0 },
        data: {
          ...node.data,
          parentId: node.parent_id ?? node.parentId ?? null,
          isRoot: !(node.parent_id ?? node.parentId),
          showAddButtons: showNodeAddButtons,
          isMobileViewport,
          startEditing: false,
          onStartEditingHandled: handleStartEditingHandled,
          onLabelChange: handleNodeLabelChange,
          onEditNote: handleNodeNoteEdit,
          onAddChild: (parentId, direction) => {
            void handleAddChildNode(parentId, direction);
          },
          onRequestDelete: (targetId) => {
            handleDeleteNodeById(targetId);
          },
        },
        selected: false,
      }));
      
      // Convert API edges to React Flow edges
      const nodeById = new Map(flowNodes.map((node) => [node.id, node]));
      const flowEdges = (currentMindmap.edges || []).map(edge => ({
        ...getStableEdgeHandles(edge, nodeById),
        id: edge.id,
        source: edge.source,
        target: edge.target,
        type: 'default',
        animated: edge.animated || false,
        style: { stroke: '#3d9168', strokeWidth: 2 },
        selected: false,
      }));
      
      setNodes(flowNodes);
      setEdges(flowEdges);
      hydratedServerSnapshotRef.current = serverSnapshotKey;

      if (shouldSaveLocally) {
        const localData = localMindmapService.loadLocalMindmapData(id);
        if (localData?.nodes?.length) {
          const parentByTarget = new Map((localData.edges || []).map((edge) => [edge.target, edge.source]));
          const localNodes = (localData.nodes || []).map((node) => ({
            id: node.id,
            type: node.type || 'mindmap',
            position: node.position || { x: 0, y: 0 },
            data: {
              ...(node.data || {}),
              parentId: node.parent_id ?? node.parentId ?? parentByTarget.get(node.id) ?? null,
              isRoot: !(node.parent_id ?? node.parentId ?? parentByTarget.get(node.id) ?? null),
              showAddButtons: showNodeAddButtons,
              isMobileViewport,
              startEditing: false,
              onStartEditingHandled: handleStartEditingHandled,
              onLabelChange: handleNodeLabelChange,
              onEditNote: handleNodeNoteEdit,
              onAddChild: (parentId, direction) => {
                void handleAddChildNode(parentId, direction);
              },
              onRequestDelete: (targetId) => {
                handleDeleteNodeById(targetId);
              },
            },
            selected: false,
          }));

          const localNodeById = new Map(localNodes.map((node) => [node.id, node]));
          const localEdges = (localData.edges || []).map((edge) => ({
            ...getStableEdgeHandles(edge, localNodeById),
            id: edge.id,
            source: edge.source,
            target: edge.target,
            type: 'default',
            animated: Boolean(edge.animated),
            style: { stroke: '#3d9168', strokeWidth: 2 },
            selected: false,
          }));

          setNodes(localNodes);
          setEdges(localEdges);
          if (localData.title) {
            setTitle(localData.title);
          }
        }
      }
    }
  }, [currentMindmap, handleNodeLabelChange, handleNodeNoteEdit, handleAddChildNode, setNodes, setEdges, showNodeAddButtons, handleStartEditingHandled, id, isLocalOnlyMap, shouldSaveLocally, isMobileViewport, handleDeleteNodeById]);

  // Save title
  const handleSaveTitle = async () => {
    setIsEditingTitle(false);
    if (!shouldSaveLocally) {
      setSyncStatus('saving');
      try {
        await updateMindmap(id, { title });
        setSyncStatus('saved');
      } catch (err) {
        console.error('Cloud title save failed:', err);
        setSyncStatus('error');
      }
    }
  };

  // Export as JSON
  const handleExportJSON = async () => {
    const data = shouldSaveLocally
      ? {
        version: '1.0',
        exportedAt: new Date().toISOString(),
        mindmap: { id, title },
        nodes: buildLocalSnapshotPayload(title, nodes, edges).nodes,
        edges: buildLocalSnapshotPayload(title, nodes, edges).edges,
      }
      : await exportMindmap(id);
    if (!data) return;

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title || 'mindmap'}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  };

  // Export as PNG
  const handleExportPNG = async () => {
    const viewportElement = document.querySelector('.react-flow__viewport');
    if (!viewportElement || nodes.length === 0) return;

    try {
      const { toPng } = await import('html-to-image');

      const bounds = getNodesBounds(nodes);
      const imageWidth = Math.max(1200, Math.ceil(bounds.width + 240));
      const imageHeight = Math.max(800, Math.ceil(bounds.height + 240));
      const viewport = getViewportForBounds(bounds, imageWidth, imageHeight, 0.1, 2, 0.15);

      const dataUrl = await toPng(viewportElement, {
        backgroundColor: '#ffffff',
        pixelRatio: 2,
        width: imageWidth,
        height: imageHeight,
        style: {
          width: `${imageWidth}px`,
          height: `${imageHeight}px`,
          transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
          transformOrigin: '0 0',
        },
      });

      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `${title || 'mindmap'}.png`;
      a.click();
    } catch (err) {
      console.error('Export PNG failed:', err);
      alert('Failed to export PNG');
    }
    setShowExportMenu(false);
  };

  const handleSaveLocal = () => {
    try {
      const payload = buildLocalSnapshotPayload(title, nodes, edges);
      localMindmapService.saveLocalMindmapData(id, payload, { updateIndex: isLocalOnlyMap });
      setShowExportMenu(false);
    } catch (err) {
      console.error('Save local failed:', err);
      alert('Failed to save local copy');
    }
  };

  const handleLoadLocal = () => {
    try {
      const parsed = localMindmapService.loadLocalMindmapData(id);
      if (!parsed) {
        alert('No local copy found for this mindmap');
        return;
      }

      const parentByTarget = new Map((parsed.edges || []).map((edge) => [edge.target, edge.source]));
      const loadedNodes = (parsed.nodes || []).map((node) => ({
        id: node.id,
        type: node.type || 'mindmap',
        position: node.position || { x: 0, y: 0 },
        selected: false,
        data: {
          ...(node.data || {}),
          parentId: node.parent_id ?? node.parentId ?? parentByTarget.get(node.id) ?? null,
          isRoot: !(node.parent_id ?? node.parentId ?? parentByTarget.get(node.id) ?? null),
          showAddButtons: showNodeAddButtons,
          isMobileViewport,
          startEditing: false,
          onStartEditingHandled: handleStartEditingHandled,
          onLabelChange: handleNodeLabelChange,
          onEditNote: handleNodeNoteEdit,
          onAddChild: (parentId, direction) => {
            void handleAddChildNode(parentId, direction);
          },
          onRequestDelete: (targetId) => {
            handleDeleteNodeById(targetId);
          },
        },
      }));

      const loadedEdges = (parsed.edges || []).map((edge) => ({
        ...edge,
        type: 'default',
        animated: Boolean(edge.animated),
        style: { stroke: '#3d9168', strokeWidth: 2 },
        selected: false,
      }));

      setNodes(loadedNodes);
      setEdges(loadedEdges);
      if (parsed.title) {
        setTitle(parsed.title);
      }
      setShowExportMenu(false);
    } catch (err) {
      console.error('Load local failed:', err);
      alert('Failed to load local copy');
    }
  };

  useEffect(() => {
    if (!id || !shouldSaveLocally) {
      return;
    }

    setSyncStatus('pending');

    const timeoutId = setTimeout(() => {
      setSyncStatus('saving');
      try {
        const payload = buildLocalSnapshotPayload(title, nodes, edges);
        localMindmapService.saveLocalMindmapData(id, payload, { updateIndex: isLocalOnlyMap });
        setSyncStatus('saved');
      } catch (err) {
        console.error('Local autosave failed:', err);
        setSyncStatus('error');
      }
    }, 350);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [id, isLocalOnlyMap, shouldSaveLocally, title, nodes, edges, buildLocalSnapshotPayload]);

  const { visibleNodes, visibleEdges } = useMemo(() => {
    const childrenByParentId = new Map();

    nodes.forEach((node) => {
      const parentId = node?.data?.parentId;
      if (!parentId) return;
      const children = childrenByParentId.get(parentId) || [];
      children.push(node.id);
      childrenByParentId.set(parentId, children);
    });

    edges.forEach((edge) => {
      const children = childrenByParentId.get(edge.source) || [];
      children.push(edge.target);
      childrenByParentId.set(edge.source, children);
    });

    const collectDescendants = (parentId, collector) => {
      const directChildren = childrenByParentId.get(parentId) || [];
      directChildren.forEach((childId) => {
        if (collector.has(childId)) {
          return;
        }
        collector.add(childId);
        collectDescendants(childId, collector);
      });
    };

    const hiddenNodeIds = new Set();
    const collapsedDescendantCountById = new Map();
    const hasChildrenById = new Set(Array.from(childrenByParentId.keys()));

    nodes.forEach((node) => {
      if (!node?.data?.collapsed) {
        return;
      }

      const descendants = new Set();
      collectDescendants(node.id, descendants);
      descendants.forEach((descendantId) => hiddenNodeIds.add(descendantId));
      collapsedDescendantCountById.set(node.id, descendants.size);
    });

    const nextVisibleNodes = nodes
      .filter((node) => !hiddenNodeIds.has(node.id))
      .map((node) => ({
        ...node,
        data: {
          ...node.data,
          hasChildren: hasChildrenById.has(node.id),
          collapsedDescendantCount: collapsedDescendantCountById.get(node.id) || 0,
          onToggleCollapse: handleToggleCollapse,
        },
      }));

    const visibleNodeIds = new Set(nextVisibleNodes.map((node) => node.id));
    const nextVisibleEdges = edges.filter(
      (edge) => visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target)
    );

    return {
      visibleNodes: nextVisibleNodes,
      visibleEdges: nextVisibleEdges,
    };
  }, [edges, handleToggleCollapse, nodes]);

  if (!isLocalOnlyMap && loading && !currentMindmap) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      <ReactFlowProvider>
        <MindmapCanvas
          id={id}
          nodes={visibleNodes}
          edges={visibleEdges}
          setNodes={setNodes}
          setEdges={setEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeDragStop={onNodeDragStop}
          title={title}
          isEditingTitle={isEditingTitle}
          setIsEditingTitle={setIsEditingTitle}
          setTitle={setTitle}
          handleSaveTitle={handleSaveTitle}
          handleAddChildNode={handleAddChildNode}
          handleDeleteNode={handleDeleteNode}
          isMobileViewport={isMobileViewport}
          mobileAddButtonsEnabled={mobileAddButtonsEnabled}
          setMobileAddButtonsEnabled={setMobileAddButtonsEnabled}
          showExportMenu={showExportMenu}
          setShowExportMenu={setShowExportMenu}
          handleExportJSON={handleExportJSON}
          handleExportPNG={handleExportPNG}
          handleSaveLocal={handleSaveLocal}
          handleLoadLocal={handleLoadLocal}
          syncStatus={syncStatus}
          shouldSaveLocally={shouldSaveLocally}
        />

        {noteModal.isOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <button
              type="button"
              className="absolute inset-0 bg-black/30"
              onClick={handleCloseNoteModal}
              aria-label="Close note modal"
            />

            <div className="relative w-full max-w-md rounded-xl border bg-white p-4 shadow-xl">
              <div className="mb-3 flex items-start justify-between gap-2">
                <h3 className="truncate text-sm font-semibold text-gray-900">
                  Note: {noteModal.nodeLabel}
                </h3>
                <button
                  type="button"
                  onClick={handleCloseNoteModal}
                  className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  aria-label="Close note modal"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>

              <textarea
                value={noteModal.value}
                onChange={(event) => setNoteModal((current) => ({ ...current, value: event.target.value }))}
                className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm text-gray-900 outline-none focus:border-primary"
                rows={6}
                placeholder="Write a note for this node..."
                autoFocus
              />

              <div className="mt-3 flex items-center justify-between gap-2">
                <p className="text-[11px] text-gray-500">Save with Ctrl/Cmd+Enter</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleCloseNoteModal}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveNodeNote}
                    className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </ReactFlowProvider>
    </div>
  );
}
